import type {
  AIActivity,
  AIAgent,
  AIAttentionItem,
  AIConversation,
  AICustomerProfile,
  AIFollowUp,
  AILead,
  AINotification,
  IntelligenceOverview,
  SalesStage,
} from '../types/contracts';

const now = Date.now();
const minAgo = (m: number) => new Date(now - m * 60000).toISOString();
const inHours = (h: number) => new Date(now + h * 3600000).toISOString();

export const JOURNEY_STAGES: SalesStage[] = ['new', 'contacted', 'qualified', 'demo', 'trial', 'negotiation', 'won'];

export const mockAgents: AIAgent[] = [
  { id: 'ag-sales', name: 'DressnMore Sales', role: 'sales', status: 'active', handled_today: 34 },
  { id: 'ag-support', name: 'DressnMore Support', role: 'support', status: 'active', handled_today: 11 },
  { id: 'ag-followup', name: 'DressnMore Follow-up', role: 'follow_up', status: 'active', handled_today: 9 },
  { id: 'ag-onboarding', name: 'DressnMore Onboarding', role: 'onboarding', status: 'active', handled_today: 4 },
];

const maryamConversation: AIConversation = {
  id: 'conv-001',
  customer_id: 'cust-001',
  customer_name: 'مريم أحمد',
  business_name: 'Atelier Noor',
  channel: 'whatsapp',
  online: true,
  last_message: 'طيب لو اشتركت النهاردة أقدر أضيف موظفة؟',
  last_message_at: minAgo(2),
  unread: 2,
  lead_score: 87,
  temperature: 'hot',
  stage: 'demo',
  handler: 'ai',
  ai_confidence: 94,
  decision: {
    intent: 'Pricing inquiry',
    lead_temperature: 'hot',
    stage: 'demo',
    recommended_action: 'Offer annual discount + confirm multi-branch support',
    reason: 'Customer asked about adding staff and subscribing today — strong purchase intent.',
    confidence: 94,
  },
  messages: [
    { id: 'm1', author: 'customer', author_name: 'مريم أحمد', body: 'عندي 3 فروع، النظام ينفع؟', sent_at: minAgo(42) },
    { id: 'm2', author: 'ai_agent', author_name: 'DressnMore Sales Agent', body: 'أكيد يا مريم 🌸 النظام يدعم الفروع المتعددة من لوحة واحدة — كل فرع له حجوزاته ومخزونه وتقاريره، وانتِ تشوفين الكل مجمع.', sent_at: minAgo(41), delivery: 'read' },
    { id: 'm3', author: 'system', author_name: 'System', body: 'Demo account created for Atelier Noor', sent_at: minAgo(40) },
    { id: 'm4', author: 'customer', author_name: 'مريم أحمد', body: 'عايزة أعرف هل الباقة الـ2000 فيها فروع متعددة؟', sent_at: minAgo(8) },
    { id: 'm5', author: 'ai_agent', author_name: 'DressnMore Sales Agent', body: 'باقة الـ2000 تشمل فرعين، ولو محتاجة الثالث نضيفه بفرق بسيط. تحبي أجهزلك عرض سعر على 3 فروع؟', sent_at: minAgo(7), delivery: 'read' },
    { id: 'm6', author: 'customer', author_name: 'مريم أحمد', body: 'طيب لو اشتركت النهاردة أقدر أضيف موظفة؟', sent_at: minAgo(2) },
    { id: 'm7', author: 'system', author_name: 'System', body: 'Follow-up scheduled for tomorrow 10:00', sent_at: minAgo(1) },
  ],
};

const saraConversation: AIConversation = {
  id: 'conv-002',
  customer_id: 'cust-002',
  customer_name: 'سارة محمود',
  business_name: 'Sara Couture',
  channel: 'whatsapp',
  online: true,
  last_message: 'الحجز مش بيظهر في الكالندر عندي',
  last_message_at: minAgo(5),
  unread: 1,
  lead_score: 61,
  temperature: 'warm',
  stage: 'trial',
  handler: 'ai',
  ai_confidence: 72,
  decision: {
    intent: 'Support — booking visibility',
    lead_temperature: 'warm',
    stage: 'trial',
    recommended_action: 'Escalate to support if not resolved in 10 min',
    reason: 'Customer on trial hit a booking issue — churn risk if unresolved.',
    confidence: 72,
  },
  messages: [
    { id: 'm1', author: 'customer', author_name: 'سارة محمود', body: 'الحجز مش بيظهر في الكالندر عندي', sent_at: minAgo(5) },
    { id: 'm2', author: 'ai_agent', author_name: 'DressnMore Support Agent', body: 'وعليكم السلام يا سارة، خليني أتأكد — الحجز اتعمل من صفحة الحجوزات ولا من الواتس؟', sent_at: minAgo(4), delivery: 'delivered' },
  ],
};

const hudaConversation: AIConversation = {
  id: 'conv-003',
  customer_id: 'cust-003',
  customer_name: 'هدى سامي',
  business_name: 'Huda Bridal',
  channel: 'instagram',
  online: false,
  last_message: 'تمام، ابعتيلي تفاصيل الاشتراك السنوي',
  last_message_at: minAgo(26),
  unread: 0,
  lead_score: 92,
  temperature: 'hot',
  stage: 'negotiation',
  handler: 'human',
  ai_confidence: 97,
  decision: {
    intent: 'Purchase — annual plan',
    lead_temperature: 'hot',
    stage: 'negotiation',
    recommended_action: 'Send annual invoice link with 2-months-free offer',
    reason: 'Customer explicitly requested annual subscription details.',
    confidence: 97,
  },
  messages: [
    { id: 'm1', author: 'customer', author_name: 'هدى سامي', body: 'فيه خصم على الاشتراك السنوي؟', sent_at: minAgo(35) },
    { id: 'm2', author: 'ai_agent', author_name: 'DressnMore Sales Agent', body: 'أهلاً يا هدى ✨ الاشتراك السنوي عليه شهرين هدية — يعني تدفعي 10 وتاخدي 12.', sent_at: minAgo(34), delivery: 'read' },
    { id: 'm3', author: 'customer', author_name: 'هدى سامي', body: 'تمام، ابعتيلي تفاصيل الاشتراك السنوي', sent_at: minAgo(26) },
    { id: 'm4', author: 'human_agent', author_name: 'Aya (Sales)', body: 'تمام يا هدى، بجهزلك رابط الدفع للسنوي دلوقتي 🙏', sent_at: minAgo(20), delivery: 'read' },
  ],
};

const laylaConversation: AIConversation = {
  id: 'conv-004',
  customer_id: 'cust-004',
  customer_name: 'ليلى حسن',
  business_name: 'Layla Fashion House',
  channel: 'website',
  online: true,
  last_message: 'هل فيه تطبيق موبايل للعملاء؟',
  last_message_at: minAgo(12),
  unread: 1,
  lead_score: 44,
  temperature: 'cold',
  stage: 'contacted',
  handler: 'ai',
  ai_confidence: 88,
  messages: [
    { id: 'm1', author: 'customer', author_name: 'ليلى حسن', body: 'هل فيه تطبيق موبايل للعملاء؟', sent_at: minAgo(12) },
  ],
};

export const mockConversations: AIConversation[] = [maryamConversation, saraConversation, hudaConversation, laylaConversation];

export const mockLeads: AILead[] = [
  { id: 'lead-001', name: 'مريم أحمد', business: 'Atelier Noor', phone: '+20 100 555 0142', location: 'Cairo', source: 'whatsapp', created_at: minAgo(60 * 24 * 4), score: 87, temperature: 'hot', stage: 'demo', last_interaction_at: minAgo(8), next_action: 'Follow-up today', interested_in: ['Multi branch', 'Reservations', 'WhatsApp'], objections: ['Price'] },
  { id: 'lead-002', name: 'هدى سامي', business: 'Huda Bridal', phone: '+20 111 209 8871', location: 'Alexandria', source: 'instagram', created_at: minAgo(60 * 24 * 9), score: 92, temperature: 'hot', stage: 'negotiation', last_interaction_at: minAgo(26), next_action: 'Send annual invoice', interested_in: ['Annual plan'], objections: [] },
  { id: 'lead-003', name: 'سارة محمود', business: 'Sara Couture', phone: '+20 122 884 3010', location: 'Giza', source: 'whatsapp', created_at: minAgo(60 * 24 * 6), score: 61, temperature: 'warm', stage: 'trial', last_interaction_at: minAgo(5), next_action: 'Resolve booking issue', interested_in: ['Reservations'], objections: ['Learning curve'] },
  { id: 'lead-004', name: 'ليلى حسن', business: 'Layla Fashion House', phone: '+20 101 773 5520', location: 'Cairo', source: 'website', created_at: minAgo(60 * 20), score: 44, temperature: 'cold', stage: 'contacted', last_interaction_at: minAgo(12), next_action: 'Qualify need', interested_in: ['Mobile app'], objections: [] },
  { id: 'lead-005', name: 'رانيا عادل', business: 'Rania Atelier', phone: '+20 100 402 9988', location: 'Mansoura', source: 'referral', created_at: minAgo(60 * 24 * 12), score: 78, temperature: 'warm', stage: 'demo', last_interaction_at: minAgo(60 * 5), next_action: 'Demo ending — ask about activation', interested_in: ['Inventory', 'Invoices'], objections: ['Price'] },
  { id: 'lead-006', name: 'دينا فؤاد', business: 'Dina Style Studio', phone: '+20 112 660 4312', location: 'Cairo', source: 'facebook', created_at: minAgo(60 * 24 * 2), score: 55, temperature: 'warm', stage: 'qualified', last_interaction_at: minAgo(60 * 9), next_action: 'Offer demo', interested_in: ['Reservations'], objections: [] },
  { id: 'lead-007', name: 'منى الشاذلي', business: 'Mona Atelier', phone: '+20 115 990 1274', location: 'Tanta', source: 'manual', created_at: minAgo(60 * 24 * 20), score: 95, temperature: 'hot', stage: 'won', last_interaction_at: minAgo(60 * 30), next_action: 'Onboarding call', interested_in: ['Full suite'], objections: [] },
  { id: 'lead-008', name: 'أمل رزق', business: 'Amal Couture', phone: '+20 106 318 7745', location: 'Cairo', source: 'whatsapp', created_at: minAgo(60 * 24 * 15), score: 30, temperature: 'cold', stage: 'lost', last_interaction_at: minAgo(60 * 24 * 3), next_action: '—', interested_in: [], objections: ['Chose competitor'] },
];

export const mockFollowUps: AIFollowUp[] = [
  { id: 'fu-001', customer_id: 'cust-001', customer_name: 'مريم أحمد', business: 'Atelier Noor', temperature: 'hot', reason: 'Demo ending', suggested_action: 'Ask about activation', due_at: inHours(-1), owner: 'DressnMore Sales Agent', status: 'overdue', conversation_id: 'conv-001' },
  { id: 'fu-002', customer_id: 'cust-005', customer_name: 'رانيا عادل', business: 'Rania Atelier', temperature: 'warm', reason: 'Demo ending in 2 days', suggested_action: 'Ask about activation', due_at: inHours(2), owner: 'DressnMore Follow-up Agent', status: 'today', conversation_id: 'conv-001' },
  { id: 'fu-003', customer_id: 'cust-002', customer_name: 'سارة محمود', business: 'Sara Couture', temperature: 'warm', reason: 'Trial check-in', suggested_action: 'Confirm booking issue resolved', due_at: inHours(4), owner: 'DressnMore Support Agent', status: 'today', conversation_id: 'conv-002' },
  { id: 'fu-004', customer_id: 'cust-003', customer_name: 'هدى سامي', business: 'Huda Bridal', temperature: 'hot', reason: 'Payment link sent', suggested_action: 'Confirm payment received', due_at: inHours(26), owner: 'DressnMore Sales Agent', status: 'upcoming', conversation_id: 'conv-003' },
  { id: 'fu-005', customer_id: 'cust-006', customer_name: 'دينا فؤاد', business: 'Dina Style Studio', temperature: 'warm', reason: 'Demo offered, no answer', suggested_action: 'Re-engage with video walkthrough', due_at: inHours(48), owner: 'DressnMore Follow-up Agent', status: 'upcoming' },
  { id: 'fu-006', customer_id: 'cust-007', customer_name: 'منى الشاذلي', business: 'Mona Atelier', temperature: 'hot', reason: 'New subscriber onboarding', suggested_action: 'Onboarding call — done', due_at: minAgo(60 * 20), owner: 'DressnMore Onboarding Agent', status: 'completed' },
  { id: 'fu-007', customer_id: 'cust-008', customer_name: 'نورهان كمال', business: 'Nourhan Bridal', temperature: 'cold', reason: 'Trial expired without upgrade', suggested_action: 'Win-back offer', due_at: minAgo(60 * 30), owner: 'DressnMore Follow-up Agent', status: 'completed' },
];

export const mockAttention: AIAttentionItem[] = [
  { id: 'at-001', kind: 'hot_lead', priority: 'high', customer_name: 'مريم', business: 'Atelier Noor', excerpt: 'عايزة أعرف هل الباقة الـ2000 فيها فروع متعددة؟', status_label: 'Hot Lead', last_interaction_at: minAgo(8), ai_confidence: 94, suggested_action: 'Answer pricing question + offer demo', conversation_id: 'conv-001' },
  { id: 'at-002', kind: 'purchase_intent', priority: 'high', customer_name: 'هدى سامي', business: 'Huda Bridal', excerpt: 'العميلة سألت عن الدفع والاشتراك السنوي', status_label: 'Purchase Intent', last_interaction_at: minAgo(26), ai_confidence: 97, suggested_action: 'Send annual invoice link', conversation_id: 'conv-003' },
  { id: 'at-003', kind: 'support_escalation', priority: 'high', customer_name: 'سارة محمود', business: 'Sara Couture', excerpt: 'العميلة واجهت مشكلة في إنشاء حجز', status_label: 'Support Escalation', last_interaction_at: minAgo(5), ai_confidence: 72, suggested_action: 'Escalate to support engineer', conversation_id: 'conv-002' },
  { id: 'at-004', kind: 'follow_up_due', priority: 'medium', customer_name: 'رانيا عادل', business: 'Rania Atelier', excerpt: 'عميلة أخذت Demo ولم تشترك منذ يومين', status_label: 'Follow-up Due', last_interaction_at: minAgo(60 * 5), ai_confidence: 81, suggested_action: 'Ask about activation', conversation_id: 'conv-001' },
  { id: 'at-005', kind: 'demo_active', priority: 'low', customer_name: 'دينا فؤاد', business: 'Dina Style Studio', excerpt: 'العميلة تستخدم حساب Demo حاليًا', status_label: 'Demo Active', last_interaction_at: minAgo(60 * 9), ai_confidence: 66, suggested_action: 'Send tips for first reservation', conversation_id: 'conv-004' },
];

export const mockNotifications: AINotification[] = [
  { id: 'n1', icon: 'ri-fire-line', title: 'Hot lead needs attention — Atelier Noor', at: minAgo(8), read: false },
  { id: 'n2', icon: 'ri-timer-flash-line', title: 'Demo expires tomorrow — Rania Atelier', at: minAgo(40), read: false },
  { id: 'n3', icon: 'ri-customer-service-line', title: 'Customer requested human support — Sara Couture', at: minAgo(55), read: false },
  { id: 'n4', icon: 'ri-error-warning-line', title: 'AI failed to answer — 1 conversation', at: minAgo(120), read: false },
  { id: 'n5', icon: 'ri-bank-card-line', title: 'Payment question detected — Huda Bridal', at: minAgo(150), read: true },
  { id: 'n6', icon: 'ri-user-add-line', title: 'New lead from website — Layla Fashion House', at: minAgo(60 * 20), read: true },
  { id: 'n7', icon: 'ri-checkbox-circle-line', title: 'Subscription activated — Mona Atelier', at: minAgo(60 * 30), read: true },
];

export const mockOverview: IntelligenceOverview = {
  kpis: [
    { id: 'new_leads', label: 'New Leads', value: 24 },
    { id: 'hot_leads', label: 'Hot Leads', value: 8 },
    { id: 'followups_due', label: 'Follow-ups Due', value: 13 },
    { id: 'demos_active', label: 'Demos Active', value: 17 },
    { id: 'trials_expiring', label: 'Trials Expiring', value: 5 },
    { id: 'need_help', label: 'Customers Needing Help', value: 4 },
  ],
  attention: mockAttention,
  live: mockConversations,
  agents: mockAgents,
  notifications: mockNotifications,
};

const profileMap: Record<string, AICustomerProfile> = {
  'cust-001': {
    id: 'cust-001', name: 'مريم أحمد', business: 'Atelier Noor', phone: '+20 100 555 0142', location: 'Cairo',
    channel: 'whatsapp', temperature: 'hot', score: 87,
    journey: JOURNEY_STAGES.map((s) => ({
      stage: s,
      state: (['new', 'contacted', 'qualified', 'demo'].includes(s) ? 'done' : s === 'trial' ? 'current' : 'upcoming') as 'done' | 'current' | 'upcoming',
    })),
    commercial: { current_plan: 'Demo', demo_expires_in_days: 3, last_activity: 'Today', invoices_count: 0, branches: 2 },
    ai_notes: { interested_in: ['Multi branch', 'Reservations', 'WhatsApp'], objections: ['Price'], next_best_action: 'Schedule follow-up' },
  },
  'cust-002': {
    id: 'cust-002', name: 'سارة محمود', business: 'Sara Couture', phone: '+20 122 884 3010', location: 'Giza',
    channel: 'whatsapp', temperature: 'warm', score: 61,
    journey: JOURNEY_STAGES.map((s) => ({
      stage: s,
      state: (['new', 'contacted', 'qualified', 'demo'].includes(s) ? 'done' : s === 'trial' ? 'current' : 'upcoming') as 'done' | 'current' | 'upcoming',
    })),
    commercial: { current_plan: 'Trial', demo_expires_in_days: 6, last_activity: 'Today', invoices_count: 0, branches: 1 },
    ai_notes: { interested_in: ['Reservations'], objections: ['Learning curve'], next_best_action: 'Resolve booking issue' },
  },
  'cust-003': {
    id: 'cust-003', name: 'هدى سامي', business: 'Huda Bridal', phone: '+20 111 209 8871', location: 'Alexandria',
    channel: 'instagram', temperature: 'hot', score: 92,
    journey: JOURNEY_STAGES.map((s) => ({
      stage: s,
      state: (['new', 'contacted', 'qualified', 'demo', 'trial'].includes(s) ? 'done' : s === 'negotiation' ? 'current' : 'upcoming') as 'done' | 'current' | 'upcoming',
    })),
    commercial: { current_plan: 'Trial', demo_expires_in_days: 2, last_activity: 'Today', invoices_count: 1, branches: 1 },
    ai_notes: { interested_in: ['Annual plan'], objections: [], next_best_action: 'Send annual invoice' },
  },
  'cust-004': {
    id: 'cust-004', name: 'ليلى حسن', business: 'Layla Fashion House', phone: '+20 101 773 5520', location: 'Cairo',
    channel: 'website', temperature: 'cold', score: 44,
    journey: JOURNEY_STAGES.map((s) => ({
      stage: s,
      state: (s === 'new' ? 'done' : s === 'contacted' ? 'current' : 'upcoming') as 'done' | 'current' | 'upcoming',
    })),
    commercial: { current_plan: '—', demo_expires_in_days: null, last_activity: 'Today', invoices_count: 0, branches: 1 },
    ai_notes: { interested_in: ['Mobile app'], objections: [], next_best_action: 'Qualify need' },
  },
};

export function mockProfile(customerId: string): AICustomerProfile {
  return profileMap[customerId] || profileMap['cust-001'];
}

const timelineMap: Record<string, AIActivity[]> = {
  'lead-001': [
    { id: 't1', at: minAgo(60 * 2 + 18), label: 'Customer contacted AI', kind: 'message' },
    { id: 't2', at: minAgo(60 * 2 + 17), label: 'AI answered pricing question', kind: 'ai_action' },
    { id: 't3', at: minAgo(60 * 2 + 15), label: 'Demo account created', kind: 'system' },
    { id: 't4', at: minAgo(60 * 2), label: 'Customer opened dashboard', kind: 'system' },
    { id: 't5', at: inHours(20), label: 'Follow-up scheduled', kind: 'scheduled' },
  ],
};

export function mockTimeline(leadId: string): AIActivity[] {
  return timelineMap[leadId] || [
    { id: 'g1', at: minAgo(60 * 5), label: 'Lead captured from channel', kind: 'system' },
    { id: 'g2', at: minAgo(60 * 4), label: 'AI sent welcome message', kind: 'ai_action' },
    { id: 'g3', at: inHours(22), label: 'Follow-up scheduled', kind: 'scheduled' },
  ];
}
